import * as React from "react";
import { useNavigate } from "react-router";
import { Container } from '@/components/ui/container';
import { Stack } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2 } from 'lucide-react';

function Main() {
  const navigate = useNavigate();

  return (
    <div className="w-full bg-background">
      {/* Introduction Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-background">
        <Container>
          <Stack spacing="lg" align="center" className="text-center max-w-4xl mx-auto">
            <Heading level="h2" className="text-3xl md:text-4xl font-bold">
              What is AI Evaluator?
            </Heading>
            <Text size="lg" variant="muted" className="leading-relaxed">
              AI Evaluator is an automated screening tool and interview software that seamlessly streamlines selection in recruitment. Candidates meet an engaged AI avatar who guides them through an interactive and structured digital interview, while collecting relevant competency data. Based on interview responses, the candidates are automatically ranked in a clear leaderboard that helps you make faster and better recruitment decisions.
            </Text>
          </Stack>
        </Container>
      </section>

      {/* Section 1: Image Left, Content Right */}
      <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
        <Container>
          <div className="flex items-center gap-12 flex-col md:flex-row">
            <div className="flex-1 flex justify-center items-center">
              <img 
                className="w-full max-w-lg h-auto rounded-2xl shadow-2xl object-cover" 
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/e2040eb2e43242fd027108a1a3dc0bbc016160d4379cd4e3cb777d6cf93e1018?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214" 
                alt="AI Evaluator" 
              />
            </div>
            <div className="flex-1 space-y-6">
              <Heading level="h3" className="text-2xl md:text-3xl font-bold">
                Revolutionize Your Recruitment Process
              </Heading>
              <Text size="lg" variant="muted" className="leading-relaxed">
                AI Evaluator eliminates tedious and time-consuming tasks by automating three critical steps in hiring:
              </Text>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  <Text>Resume Filtering</Text>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  <Text>Candidate Pre-screening</Text>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  <Text>Preliminary Fit Assessment</Text>
                </li>
              </ul>
              <Text size="lg" variant="muted" className="leading-relaxed">
                Our cutting-edge automation tools save you time, simplify your workflow, and ensure you focus on the candidates who truly matter. Accelerate your hiring, improve efficiency, and make smarter decisions effortlessly with AI Evaluator.
              </Text>
            </div>
          </div>
        </Container>
      </section>

      {/* Section 2: Image Right, Content Left */}
      <section className="py-12 md:py-16 lg:py-20 bg-background">
        <Container>
          <div className="flex items-center gap-12 flex-col md:flex-row-reverse">
            <div className="flex-1 flex justify-center items-center">
              <img 
                className="w-full max-w-lg h-auto rounded-2xl shadow-2xl object-cover" 
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/534abf557c264b249544b9f4c9619d6f6e344b6d5859b70d2020991910ba8ee0?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214" 
                alt="Get Started" 
              />
            </div>
            <div className="flex-1 space-y-6">
              <Heading level="h3" className="text-2xl md:text-3xl font-bold">
                Quickly Identify Your Top Candidates
              </Heading>
              <Text size="lg" variant="muted" className="leading-relaxed">
                With intuitive settings, a scientifically validated competency framework, and objective mechanical assessment, AI Evaluator provides you with a data-driven, unbiased foundation for decision-making—no matter how many applicants you have.
              </Text>
              <Text size="lg" variant="muted" className="leading-relaxed">
                Effortlessly screen and compare candidates on a level playing field, make informed hiring decisions, and confidently choose who to invite for personal interviews.
              </Text>
              <Button
                variant="default"
                size="lg"
                onClick={() => navigate('/login')}
              >
                Get Started
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center p-8 bg-card border-border/50 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                <div className="text-5xl font-bold text-primary mb-4">2X</div>
                <Text className="text-lg font-semibold">Faster Recruiting</Text>
              </CardContent>
            </Card>
            <Card className="text-center p-8 bg-card border-border/50 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                <div className="text-5xl font-bold text-primary mb-4">30%</div>
                <Text className="text-lg font-semibold">Time Reduction in Hiring Candidates</Text>
              </CardContent>
            </Card>
            <Card className="text-center p-8 bg-card border-border/50 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                <div className="text-5xl font-bold text-primary mb-4">20%</div>
                <Text className="text-lg font-semibold">Higher Retention Rate</Text>
              </CardContent>
            </Card>
          </div>
        </Container>
      </section>

      {/* Section 3: Compliant Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-background">
        <Container>
          <div className="flex items-center gap-12 flex-col md:flex-row">
            <div className="flex-1 flex justify-center items-center">
              <img 
                className="w-full max-w-lg h-auto rounded-2xl shadow-2xl object-cover" 
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/95fdae8606eab1fca9a27e1511f2c905de1789c5c12fb5cf28ee460e475261f3?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214" 
                alt="Compliant and verified"
              />
            </div>
            <div className="flex-1 space-y-6">
              <Heading level="h3" className="text-2xl md:text-3xl font-bold">
                Compliant and scientifically verified
              </Heading>
              <Text size="lg" variant="muted" className="leading-relaxed">
                AI Evaluator uses artificial intelligence for a smooth, efficient and interactive candidate dialogue. The AI tool that handles candidate data, interview responses and shear results.
              </Text>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default Main;